import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const StatisticsPanel = ({ data, loading }) => {
  const [selectedMetric, setSelectedMetric] = useState('close');
  const [analysisType, setAnalysisType] = useState('basic');

  const metricOptions = [
    { value: 'close', label: 'Close Price' },
    { value: 'volume', label: 'Volume' },
    { value: 'openInterest', label: 'Open Interest' },
    { value: 'change', label: 'Daily Change' },
    { value: 'changePercent', label: 'Daily Change %' }
  ];

  const analysisOptions = [
    { value: 'basic', label: 'Basic Statistics' },
    { value: 'volatility', label: 'Volatility Analysis' },
    { value: 'correlation', label: 'Correlation Analysis' },
    { value: 'seasonal', label: 'Seasonal Patterns' }
  ];

  const statistics = useMemo(() => {
    if (!data || data?.length === 0) return null;

    const values = data?.map(item => item?.[selectedMetric])?.filter(val => val !== null && val !== undefined);
    
    if (values?.length === 0) return null;

    const sorted = [...values]?.sort((a, b) => a - b);
    const sum = values?.reduce((acc, val) => acc + val, 0);
    const mean = sum / values?.length;
    const variance = values?.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / values?.length;
    const stdDev = Math.sqrt(variance);

    return {
      count: values?.length,
      min: Math.min(...values),
      max: Math.max(...values),
      mean: mean,
      median: sorted?.[Math.floor(sorted?.length / 2)],
      stdDev: stdDev,
      variance: variance,
      q1: sorted?.[Math.floor(sorted?.length * 0.25)],
      q3: sorted?.[Math.floor(sorted?.length * 0.75)],
      range: Math.max(...values) - Math.min(...values),
      skewness: calculateSkewness(values, mean, stdDev),
      kurtosis: calculateKurtosis(values, mean, stdDev)
    };
  }, [data, selectedMetric]);

  function calculateSkewness(values, mean, stdDev) {
    if (stdDev === 0) return 0;
    const n = values?.length;
    const sum = values?.reduce((acc, val) => acc + Math.pow((val - mean) / stdDev, 3), 0);
    return (n / ((n - 1) * (n - 2))) * sum;
  }

  function calculateKurtosis(values, mean, stdDev) {
    if (stdDev === 0) return 0;
    const n = values?.length;
    const sum = values?.reduce((acc, val) => acc + Math.pow((val - mean) / stdDev, 4), 0);
    return (n * (n + 1) / ((n - 1) * (n - 2) * (n - 3))) * sum - (3 * (n - 1) * (n - 1)) / ((n - 2) * (n - 3));
  }

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return '-';
    return Number(value)?.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const renderBasicStats = () => {
    if (!statistics) return null;

    const stats = [
      { label: 'Count', value: statistics?.count, format: 0 },
      { label: 'Mean', value: statistics?.mean, format: 4 },
      { label: 'Median', value: statistics?.median, format: 4 },
      { label: 'Minimum', value: statistics?.min, format: 4 },
      { label: 'Maximum', value: statistics?.max, format: 4 },
      { label: 'Range', value: statistics?.range, format: 4 },
      { label: 'Std Deviation', value: statistics?.stdDev, format: 4 },
      { label: 'Variance', value: statistics?.variance, format: 4 },
      { label: 'Q1 (25%)', value: statistics?.q1, format: 4 },
      { label: 'Q3 (75%)', value: statistics?.q3, format: 4 },
      { label: 'Skewness', value: statistics?.skewness, format: 4 },
      { label: 'Kurtosis', value: statistics?.kurtosis, format: 4 }
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {stats?.map((stat) => (
          <div key={stat?.label} className="bg-muted/30 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">{stat?.label}</div>
            <div className="text-lg font-semibold text-foreground">
              {formatNumber(stat?.value, stat?.format)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderVolatilityAnalysis = () => {
    if (!data || data?.length < 2) return null;

    const returns = [];
    for (let i = 1; i < data?.length; i++) {
      const current = data?.[i]?.[selectedMetric];
      const previous = data?.[i - 1]?.[selectedMetric];
      if (current && previous) {
        returns?.push((current - previous) / previous);
      }
    }

    if (returns?.length === 0) return null;

    const volatility = Math.sqrt(returns?.reduce((acc, ret) => acc + ret * ret, 0) / returns?.length);
    const annualizedVol = volatility * Math.sqrt(252); // Assuming 252 trading days
    let maxDrawdown = calculateMaxDrawdown(data?.map(item => item?.[selectedMetric]));

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="text-sm text-muted-foreground mb-2">Daily Volatility</div>
          <div className="text-2xl font-bold text-foreground">
            {formatNumber(volatility * 100, 2)}%
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="text-sm text-muted-foreground mb-2">Annualized Volatility</div>
          <div className="text-2xl font-bold text-foreground">
            {formatNumber(annualizedVol * 100, 2)}%
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="text-sm text-muted-foreground mb-2">Max Drawdown</div>
          <div className="text-2xl font-bold text-error">
            {formatNumber(maxDrawdown * 100, 2)}%
          </div>
        </div>
      </div>
    );
  };

  const calculateMaxDrawdown = (prices) => {
    let maxDrawdown = 0;
    let peak = prices?.[0];

    for (let i = 1; i < prices?.length; i++) {
      if (prices?.[i] > peak) {
        peak = prices?.[i];
      } else {
        const drawdown = (peak - prices?.[i]) / peak;
        maxDrawdown = Math.max(maxDrawdown, drawdown);
      }
    }

    return maxDrawdown;
  };

  const renderCorrelationAnalysis = () => {
    return (
      <div className="text-center py-8">
        <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">
          Correlation analysis requires multiple data series. Select additional contracts to compare.
        </p>
      </div>
    );
  };

  const renderSeasonalPatterns = () => {
    return (
      <div className="text-center py-8">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">
          Seasonal pattern analysis requires at least one full year of data.
        </p>
      </div>
    );
  };

  const renderAnalysis = () => {
    switch (analysisType) {
      case 'basic':
        return renderBasicStats();
      case 'volatility':
        return renderVolatilityAnalysis();
      case 'correlation':
        return renderCorrelationAnalysis();
      case 'seasonal':
        return renderSeasonalPatterns();
      default:
        return renderBasicStats();
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-sm">
        <div className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Calculating statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-border gap-4">
        <div className="flex items-center gap-2">
          <Icon name="BarChart3" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Statistical Analysis</h3>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Select
            options={metricOptions}
            value={selectedMetric}
            onChange={setSelectedMetric}
            className="w-40"
          />
          <Select
            options={analysisOptions}
            value={analysisType}
            onChange={setAnalysisType}
            className="w-48"
          />
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        {data && data?.length > 0 ? (
          renderAnalysis()
        ) : (
          <div className="text-center py-8">
            <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No data available for statistical analysis</p>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Analysis based on {data?.length || 0} data points
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Stats
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;